import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, ShieldCheck, Star, ArrowRight, Lock, Award, PlayCircle, ChevronDown, Users } from "lucide-react";
import { toast } from "sonner";
import { Nav, Footer } from "./Nav";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { COURSES, CERT_ICON, BIO, REAL_TESTIMONIALS, SHARED_FAQ, GUARANTEE_30, NLP_PRAC_HOURS } from "./salesContent";
import { EventPromo } from "./EventPromo";
import { Seo, SITE_URL } from "./Seo";
import { captureAttribution, getAttribution } from "../lib/attribution";
import { initPixel, pixelTrack } from "../lib/pixel";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 });

export default function SalesPage() {
  const { id } = useParams();
  const c = COURSES[id];
  const navigate = useNavigate();
  const { user, openAuth, refresh } = useAuth();
  const [busy, setBusy] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [eb, setEb] = useState(null);
  const stackRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  const [pendingEnrol, setPendingEnrol] = useState(null);
  const resumedRef = useRef(false);
  const resumeCheckoutRef = useRef(null);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", mobile: "" });

  useEffect(() => {
    api.get(`/course-video/${id}`).then((r) => setVideoUrl(r.data.video_url)).catch(() => {});
  }, [id]);

  // Paid-ad attribution + Meta Pixel PageView (NLP Practitioner only).
  useEffect(() => {
    captureAttribution();
    if (id === "nlp_practitioner") {
      initPixel();
      pixelTrack("PageView");
    }
  }, [id]);

  useEffect(() => {
    if (id === "nlp_practitioner") {
      api.get("/event/nlp-november").then((r) => setEb(r.data.early_bird)).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    const vt = (COURSES[id]?.valueStack || []).reduce((s, [, v]) => s + v, 0);
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setRevealed(true);
        const start = performance.now(), dur = 1100;
        const tick = (now) => {
          const p = Math.min(1, (now - start) / dur);
          setDisplayValue(Math.round(vt * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        io.disconnect();
      }
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [id]);

  // After any successful authentication (Google OAuth return, or email login/register),
  // restore the pending enrolment and resume checkout exactly once -> straight to PayPal.
  useEffect(() => {
    if (!user || resumedRef.current) return;
    let p = null;
    try { p = JSON.parse(localStorage.getItem("ely_resume_enrol") || "null"); } catch {}
    if (!p || p.path !== window.location.pathname) return;
    if (Date.now() - (p.ts || 0) > 30 * 60 * 1000) { localStorage.removeItem("ely_resume_enrol"); return; }
    resumedRef.current = true;
    localStorage.removeItem("ely_resume_enrol");  // clear BEFORE resuming to prevent duplicate checkouts
    if (resumeCheckoutRef.current) resumeCheckoutRef.current(p.lookup_key, p.plan);
  }, [user]);

  if (!c) { navigate("/"); return null; }

  const vsrc = videoUrl ? (videoUrl.startsWith("http") ? videoUrl : `${BACKEND}${videoUrl}`) : null;
  const valueTotal = c.valueStack.reduce((s, [, v]) => s + v, 0);

  const ebActive = !!(eb && eb.active);
  const priceFull = ebActive ? eb.full : c.full;
  const priceMonthly = ebActive ? eb.monthly : c.monthly;
  const lkFull = ebActive ? eb.lk_full : c.lkFull;
  const lk6 = ebActive ? eb.lk_6mo : c.lk6;

  const checkout = async (lookup_key, plan) => {
    if (!user) {
      // Persist the pending enrolment so it can resume automatically after authentication.
      try { localStorage.setItem("ely_resume_enrol", JSON.stringify({ lookup_key, plan, path: window.location.pathname, ts: Date.now() })); } catch {}
      openAuth("login");
      toast("Create a free account to enrol — it takes a moment.");
      return;
    }
    setBusy(lookup_key);
    try {
      if (id === "nlp_practitioner") pixelTrack("InitiateCheckout");
      const { data } = await api.post("/paypal/checkout", { lookup_key, origin_url: window.location.origin, attribution: getAttribution() });
      if (data.url) window.location.href = data.url;
      else { toast.error("Could not start checkout."); setBusy(""); }
    } catch { toast.error("Could not start checkout."); setBusy(""); }
  };
  resumeCheckoutRef.current = checkout;

  // Capture name/email/mobile as a recoverable lead BEFORE any enrolment/checkout step.
  const startEnrol = (lookup_key, plan) => {
    if (user && user.mobile) { checkout(lookup_key, plan); return; }
    setPendingEnrol({ lookup_key, plan });
    setLeadForm({ name: user?.name || "", email: user?.email || "", mobile: "" });
    setLeadOpen(true);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    const name = leadForm.name.trim(), email = leadForm.email.trim(), mobile = leadForm.mobile.trim();
    if (!name || !email || !/^\S+@\S+\.\S+$/.test(email) || mobile.replace(/\D/g, "").length < 7) {
      toast.error("Please enter your name, a valid email and mobile number.");
      return;
    }
    setLeadBusy(true);
    try {
      await api.post("/leads", { name, email, mobile, item_id: id, source: "nlp_sales", attribution: getAttribution() });
      if (id === "nlp_practitioner") pixelTrack("Lead");
      if (user) { try { await refresh(); } catch {} }
      setLeadOpen(false);
      setLeadBusy(false);
      if (pendingEnrol) checkout(pendingEnrol.lookup_key, pendingEnrol.plan);
    } catch {
      setLeadBusy(false);
      toast.error("Could not save your details. Please try again.");
    }
  };

  const CtaButtons = ({ testid }) => (
    <div style={{ display: "grid", gap: ".6rem", maxWidth: 420 }}>
      {ebActive && (
        <div data-testid="eb-flag" style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", background: "rgba(176,86,58,.16)", border: "1px solid #6b3a2c", color: "#e8a08a", fontWeight: 800, fontSize: ".8rem", padding: ".4rem .7rem", borderRadius: 999, justifySelf: "start" }}>
          🔥 Early-bird {eb.discount_pct}% off · only {eb.remaining} of {eb.seats} seats left
        </div>
      )}
      <button className="btn" data-testid={`${testid}-full`} disabled={busy === lkFull}
        onClick={() => startEnrol(lkFull, "full")}
        style={{ background: "#C7921E", color: "#161616", fontWeight: 800, justifyContent: "center", padding: ".9rem 1.4rem", fontSize: "1.02rem" }}>
        {busy === lkFull ? "Starting…" : <>Enrol in full — {gbp(priceFull)}{ebActive && <span style={{ textDecoration: "line-through", opacity: .6, marginLeft: 6, fontWeight: 600 }}>{gbp(c.full)}</span>} <ArrowRight size={17} /></>}
      </button>
      <button className="btn btn-gold-outline" data-testid={`${testid}-6mo`} disabled={busy === lk6}
        onClick={() => startEnrol(lk6, "installment")}
        style={{ justifyContent: "center", padding: ".8rem 1.4rem" }}>
        {busy === lk6 ? "Starting…" : <>or {gbp(priceMonthly)}/month for 6 months</>}
      </button>
      <p className="muted" style={{ fontSize: ".78rem", textAlign: "center", margin: 0 }}>
        Secure checkout by PayPal · 30-day money-back · Passing Guarantee
      </p>
    </div>
  );

  const CtaBand = ({ line, testid }) => (
    <section className="sec" style={{ padding: "2.4rem 0", background: "linear-gradient(180deg,#17160f,#141414)", borderTop: "1px solid #2a271f", borderBottom: "1px solid #2a271f" }}>
      <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "1.2rem", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ color: "#fff", margin: 0, maxWidth: "26ch", fontSize: "1.3rem" }}>{line}</h3>
        <CtaButtons testid={testid} />
      </div>
    </section>
  );

  const SectionHead = ({ kicker, children }) => (
    <>
      {kicker && <span className="badge-gold">{kicker}</span>}
      <h2 style={{ color: "#fff", margin: ".6rem 0 1.2rem", fontSize: "clamp(1.6rem,3vw,2.3rem)", maxWidth: "20ch" }}>{children}</h2>
    </>
  );

  const seoTitle = id === "nlp_practitioner"
    ? "NLP Practitioner Certification Training — 120 Hours, 7 Live Days"
    : `${c.title} Certification Training`;
  const seoDesc = id === "nlp_practitioner"
    ? "A structured 120-hour NLP Practitioner training programme built around 7 live training days — live instruction, guided pre-study, supervised and independent practice, and assessment. Price £1,440. Cohorts limited to a maximum of 20 participants."
    : `${c.title} certification training with Ely Wellbeing — expert-led, blending live training with guided study and practice.`;
  const courseLd = {
    "@context": "https://schema.org", "@type": "Course",
    name: `${c.title} Certification Training`,
    description: seoDesc,
    provider: { "@type": "Organization", name: "Ely Wellbeing", sameAs: SITE_URL },
  };
  if (id === "nlp_practitioner") {
    courseLd.hasCourseInstance = {
      "@type": "CourseInstance",
      courseMode: ["Onsite", "Online"],
      courseWorkload: "PT120H",
      location: { "@type": "Place", name: "Nashat African Cultural Centre", address: "101 Church St, Croydon CR0 1RN" },
    };
  }
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: SHARED_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <div className="salespg">
      <Seo title={seoTitle} description={seoDesc} path={`/nlp/${id}`} type="product" jsonLd={[courseLd, faqLd]} />
      <Nav />

      {/* Sticky enrol bar */}
      <div data-testid="sticky-cta" style={{ position: "sticky", top: "54px", marginTop: "54px", zIndex: 40, background: "rgba(16,15,13,.96)", backdropFilter: "blur(10px)", borderBottom: "1px solid #2a271f" }}>
        <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: ".55rem 0", gap: "1rem" }}>
          <strong style={{ color: "#f0c869", fontSize: ".92rem" }}>{c.award} · {c.certs.length} certifications · {gbp(priceFull)}{ebActive && <span style={{ color: "#e8a08a", fontWeight: 700, marginLeft: 6 }}>· {eb.discount_pct}% early-bird</span>}</strong>
          <a href="#enrol" className="btn" data-testid="sticky-enrol-btn" style={{ background: "#C7921E", color: "#161616", fontWeight: 800, padding: ".5rem 1.1rem" }}>Enrol now <ArrowRight size={15} /></a>
        </div>
      </div>

      {/* HERO */}
      <section className="hero" data-testid="sales-hero"
        style={{ background: "radial-gradient(1100px 520px at 75% -10%,rgba(199,146,30,.4),transparent 55%),linear-gradient(180deg,#1b1a15,#121212)" }}>
        <div className="wrap" style={{ paddingTop: "3rem", paddingBottom: "3rem" }}>
          <span className="badge-gold">{c.kicker}</span>
          <h1 style={{ marginTop: "1rem", maxWidth: "20ch", fontSize: "clamp(2rem,4.6vw,3.3rem)", lineHeight: 1.08 }}>{c.title}</h1>
          <p className="lead" data-testid="sales-hook" style={{ maxWidth: "62ch", marginTop: "1rem" }}>{c.hook}</p>
          <p className="lead" style={{ maxWidth: "62ch", color: "#e7d9c9" }}>{c.subhook}</p>

          {vsrc && (
            <div data-testid="sales-video" style={{ margin: "1.6rem 0", maxWidth: 760 }}>
              <video src={vsrc} controls playsInline poster=""
                style={{ width: "100%", borderRadius: 16, border: "1px solid #3a352c", boxShadow: "0 24px 70px rgba(0,0,0,.5)" }} />
              <p className="muted" style={{ fontSize: ".82rem", marginTop: ".5rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                <PlayCircle size={15} /> Watch Evan explain it — or keep reading the full story below.
              </p>
            </div>
          )}

          <div style={{ marginTop: "1.2rem" }}><CtaButtons testid="hero-cta" /></div>
          <div style={{ display: "flex", gap: "1.4rem", flexWrap: "wrap", marginTop: "1.1rem", alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", color: "#f0c869", fontWeight: 600 }}>
              <Star size={16} fill="#f0c869" /> {c.rating} · {c.reviews} certified reviews
            </span>
            <span className="muted" style={{ fontSize: ".85rem" }}>{c.duration}</span>
          </div>
        </div>
      </section>

      {id === "nlp_practitioner" && <EventPromo ctaHref="#enrol" />}

      {/* PROBLEM */}
      <section className="sec pad"><div className="wrap" style={{ maxWidth: "70ch" }}>
        <SectionHead kicker="DOES THIS SOUND FAMILIAR?">{c.problemHeadline}</SectionHead>
        {c.problem.map((p, i) => <p key={i} style={{ color: "#cabfae", marginBottom: ".9rem", lineHeight: 1.7 }}>{p}</p>)}
      </div></section>

      {/* VEHICLE SHIFT */}
      <section className="sec pad" style={{ background: "#17160f" }}><div className="wrap" style={{ maxWidth: "70ch" }}>
        <SectionHead kicker="THE SHIFT">{c.vehicleHeadline}</SectionHead>
        {c.vehicle.map((p, i) => <p key={i} style={{ color: "#e7d9c9", marginBottom: ".9rem", lineHeight: 1.7 }}>{p}</p>)}
      </div></section>

      <CtaBand testid="band1-cta" line={`Ready to learn how your mind really works? Enrol in the ${c.award}.`} />

      {/* CERT VALUE SHOWCASE */}
      <section className="sec pad" data-testid="cert-showcase"><div className="wrap">
        <SectionHead kicker="ONE ENROLMENT — EVERYTHING INCLUDED">You don't earn one certification. You earn {c.certs.length}.</SectionHead>
        <p className="muted" style={{ maxWidth: "60ch", marginBottom: "1.6rem" }}>Most schools charge separately for each of these. Here, they're woven into a single, coherent training.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "1rem" }}>
          {c.certs.map((ct) => {
            const I = CERT_ICON[ct.key].icon, col = CERT_ICON[ct.key].color;
            return (
              <div key={ct.name} style={{ textAlign: "center", padding: "1.6rem 1rem", background: "#1b1a17", border: "1px solid #3a352c", borderRadius: 14 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: col, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto .8rem" }}>
                  <I size={28} color="#fff" />
                </div>
                <strong style={{ color: "#ffffff", display: "block", fontSize: ".98rem", lineHeight: 1.25 }}>{ct.name}</strong>
                <span style={{ color: "#b9ad9c", fontSize: ".82rem" }}>{ct.sub}</span>
                <div style={{ marginTop: ".6rem", fontSize: ".72rem", color: "#f0c869", display: "flex", alignItems: "center", justifyContent: "center", gap: ".3rem" }}>
                  <Award size={13} /> Certification
                </div>
              </div>
            );
          })}
        </div>
      </div></section>

      {/* OUTCOMES */}
      <section className="sec pad" style={{ background: "#17160f" }}><div className="wrap" style={{ maxWidth: "72ch" }}>
        <SectionHead kicker="SIX MONTHS FROM NOW">{c.outcomesHeadline}</SectionHead>
        <div style={{ display: "grid", gap: ".9rem" }}>
          {c.outcomes.map((o, i) => (
            <div key={i} style={{ display: "flex", gap: ".7rem", alignItems: "flex-start" }}>
              <Check size={20} color="#C7921E" style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ color: "#e7d9c9", margin: 0, lineHeight: 1.6 }}><strong style={{ color: "#fff" }}>{o.bold}</strong> {o.text}</p>
            </div>
          ))}
        </div>
        <p style={{ color: "#f0c869", marginTop: "1.4rem", fontStyle: "italic", maxWidth: "58ch" }}>{c.promiseLine}</p>
      </div></section>

      {/* 120 HOURS programme structure (NLP Practitioner only) */}
      {id === "nlp_practitioner" && (
        <section className="sec pad" data-testid="hours-breakdown" style={{ background: "#17160f" }}><div className="wrap" style={{ maxWidth: "86ch" }}>
          <span className="badge-gold">THE PROGRAMME STRUCTURE</span>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "1rem", margin: ".6rem 0 .4rem" }}>
            <span style={{ fontFamily: "'Playfair Display',serif", fontWeight: 800, color: "#f0c869", fontSize: "clamp(3rem,8vw,5rem)", lineHeight: 1 }}>{NLP_PRAC_HOURS.total}</span>
            <span style={{ color: "#fff", fontSize: "clamp(1.3rem,3vw,2rem)", fontWeight: 700 }}>Hours of Learning, Practice &amp; Assessment</span>
          </div>
          <p style={{ color: "#f0c869", fontWeight: 700, margin: "0 0 1rem", fontSize: ".95rem" }} data-testid="hours-headline">{NLP_PRAC_HOURS.headline}</p>
          <p style={{ color: "#e7d9c9", lineHeight: 1.7, maxWidth: "66ch", marginBottom: "1.2rem" }}>{NLP_PRAC_HOURS.lead}</p>

          {/* Learn -> Demonstrate journey */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", alignItems: "center", marginBottom: "1.6rem" }} data-testid="hours-journey">
            {NLP_PRAC_HOURS.journey.map((step, i) => (
              <React.Fragment key={step}>
                <span style={{ background: "rgba(199,146,30,.14)", border: "1px solid #6b5a2c", color: "#f0c869", fontWeight: 700, fontSize: ".82rem", padding: ".35rem .8rem", borderRadius: 999 }}>{step}</span>
                {i < NLP_PRAC_HOURS.journey.length - 1 && <span style={{ color: "#C7921E" }}>→</span>}
              </React.Fragment>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "1rem" }}>
            {NLP_PRAC_HOURS.parts.map(([hrs, title, sub], i) => (
              <div key={i} data-testid={`hours-part-${i}`} className="hub-card" style={{ padding: "1.4rem", borderLeft: "3px solid #C7921E" }}>
                <div style={{ color: "#f0c869", fontWeight: 800, fontSize: "1.5rem", fontFamily: "'Playfair Display',serif" }}>{hrs}</div>
                <strong style={{ color: "#fff", display: "block", margin: ".3rem 0 .3rem", fontSize: "1rem" }}>{title}</strong>
                <span className="muted" style={{ fontSize: ".86rem", lineHeight: 1.5 }}>{sub}</span>
              </div>
            ))}
          </div>

          <p className="muted" style={{ marginTop: "1.2rem", fontSize: ".9rem" }}>
            60 hrs live instructor-led &nbsp;+&nbsp; 20 hrs guided pre-study &nbsp;+&nbsp; 20 hrs supervised practice &nbsp;+&nbsp; 10 hrs independent practice &nbsp;+&nbsp; 10 hrs assessment &nbsp;=&nbsp; <strong style={{ color: "#f0c869" }}>~120 hours total</strong>
          </p>
          <p className="muted" style={{ marginTop: ".6rem", fontSize: ".85rem", fontStyle: "italic" }} data-testid="hours-note">{NLP_PRAC_HOURS.note}</p>
          <p style={{ marginTop: "1rem", color: "#e7d9c9", fontSize: ".9rem", display: "flex", gap: ".5rem", alignItems: "flex-start" }} data-testid="cohort-note">
            <Users size={16} color="#C7921E" style={{ flexShrink: 0, marginTop: 3 }} /> {NLP_PRAC_HOURS.cohort}
          </p>
        </div></section>
      )}

      {/* CURRICULUM */}
      <section className="sec pad"><div className="wrap" style={{ maxWidth: "74ch" }}>
        <SectionHead kicker="WHAT YOU'LL LEARN">The journey, module by module</SectionHead>
        <div data-testid="curriculum-timeline" style={{ marginTop: ".4rem" }}>
          {c.curriculum.map(([m, o], i) => {
            const last = i === c.curriculum.length - 1;
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "46px 1fr", columnGap: "1.1rem", alignItems: "stretch" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", background: "#1b1a17", border: "2px solid #C7921E", color: "#f0c869", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "'Playfair Display',serif", fontSize: ".95rem" }}>{String(i + 1).padStart(2, "0")}</div>
                  {!last && <div style={{ flex: 1, width: 2, background: "linear-gradient(#C7921E,#3a352c)", minHeight: 20 }} />}
                </div>
                <div style={{ paddingBottom: last ? 0 : "1.5rem", paddingTop: ".4rem" }}>
                  <strong style={{ color: "#fff", display: "block", fontSize: "1.04rem" }}>{m}</strong>
                  <p className="muted" style={{ margin: ".25rem 0 0", fontSize: ".9rem", lineHeight: 1.6 }}>{o}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div></section>

      <CtaBand testid="band2-cta" line="This is the exact path. Claim your place now." />

      {/* TRAINER BIO */}
      <section className="sec pad" data-testid="trainer-bio" style={{ background: "#17160f" }}><div className="wrap" style={{ maxWidth: "74ch" }}>
        <SectionHead kicker="MEET YOUR TRAINER">{BIO.name}</SectionHead>
        <p style={{ color: "#f0c869", fontWeight: 600, marginTop: "-.6rem" }}>{BIO.roles}</p>
        <p style={{ color: "#fff", fontSize: "1.15rem", fontStyle: "italic", margin: "1rem 0" }}>{BIO.lede}</p>
        {BIO.paras.map((p, i) => <p key={i} style={{ color: "#cabfae", marginBottom: ".8rem", lineHeight: 1.7 }}>{p}</p>)}
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", margin: "1rem 0" }}>
          {BIO.disciplines.map((d) => <span key={d} className="pill">{d}</span>)}
        </div>
        <div style={{ display: "grid", gap: ".5rem", marginTop: "1rem", borderLeft: "2px solid #C7921E", paddingLeft: "1rem" }}>
          {BIO.timeline.map(([y, t]) => (
            <div key={y} style={{ display: "flex", gap: ".8rem" }}><strong style={{ color: "#f0c869", minWidth: 72 }}>{y}</strong><span className="muted">{t}</span></div>
          ))}
        </div>
        <p style={{ color: "#fff", marginTop: "1.2rem", fontStyle: "italic" }}>"{BIO.philosophy}"</p>
      </div></section>

      {/* VALUE STACK + PRICE */}
      <section className="sec pad" id="enrol" data-testid="enrol-section"><div className="wrap" style={{ maxWidth: "62ch" }}>
        <SectionHead kicker="THE VALUE STACK">Here's everything you receive</SectionHead>
        <div className="hub-card" ref={stackRef} style={{ padding: "1.4rem" }}>
          {c.valueStack.map(([item, val]) => (
            <div key={item} style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: ".55rem 0", borderBottom: "1px solid #2a271f" }}>
              <span style={{ color: "#e7d9c9", display: "flex", gap: ".5rem" }}><Check size={18} color="#C7921E" style={{ flexShrink: 0 }} /> {item}</span>
              <span className="muted" style={{ whiteSpace: "nowrap" }}>{gbp(val)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: ".8rem 0 0", fontWeight: 700 }}>
            <span style={{ color: "#fff" }}>Total value</span>
            <span style={{ color: "#fff", textDecoration: "line-through", opacity: .7 }} data-testid="value-total">{gbp(revealed ? displayValue : valueTotal)}</span>
          </div>
        </div>

        <div style={{ textAlign: "center", margin: "1.8rem 0 1rem" }}>
          <p className="muted" style={{ margin: 0 }}>Your investment today</p>
          <div key={revealed ? "r" : "u"} className={revealed ? "price-pop" : ""} data-testid="price-reveal" style={{ fontSize: "clamp(2.4rem,6vw,3.4rem)", color: "#f0c869", fontWeight: 800, fontFamily: "'Playfair Display',serif" }}>
            {gbp(priceFull)}
            {ebActive && <span style={{ fontSize: "1.4rem", textDecoration: "line-through", opacity: .55, marginLeft: 10, fontFamily: "Arial,sans-serif" }}>{gbp(c.full)}</span>}
          </div>
          {ebActive && <p style={{ margin: ".2rem 0 0", color: "#e8a08a", fontWeight: 700 }}>Early-bird {eb.discount_pct}% off — first {eb.seats} seats ({eb.remaining} left)</p>}
          <p className="muted" style={{ margin: 0 }}>or {gbp(priceMonthly)}/month for 6 months</p>
        </div>
        {c.prereq && <p className="muted" style={{ textAlign: "center", fontSize: ".82rem", marginBottom: "1rem" }}>{c.prereq}</p>}
        <div style={{ display: "flex", justifyContent: "center" }}><CtaButtons testid="enrol-cta" /></div>
      </div></section>

      {/* GUARANTEES */}
      <section className="sec pad" data-testid="guarantees" style={{ background: "#17160f" }}><div className="wrap" style={{ maxWidth: "72ch" }}>
        <SectionHead kicker="YOUR RISK IS COVERED — TWICE">Two guarantees, so the only risk is not starting</SectionHead>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: "1rem" }}>
          <div className="hub-card" style={{ padding: "1.4rem" }}>
            <ShieldCheck size={26} color="#C7921E" />
            <h3 style={{ color: "#fff", margin: ".6rem 0" }}>{GUARANTEE_30.title}</h3>
            <p className="muted" style={{ lineHeight: 1.6 }}>{GUARANTEE_30.body}</p>
          </div>
          <div className="hub-card" style={{ padding: "1.4rem" }}>
            <Award size={26} color="#C7921E" />
            <h3 style={{ color: "#fff", margin: ".6rem 0" }}>The Passing Guarantee</h3>
            <p className="muted" style={{ lineHeight: 1.6 }}>Complete the training, pass your certification, and we refund 100% of your course fee. Pass, and your certification effectively becomes free. (Applies to pay-in-full enrolments.)</p>
          </div>
        </div>
      </div></section>

      {/* TESTIMONIALS */}
      <section className="sec pad" data-testid="testimonials"><div className="wrap">
        <SectionHead kicker="WHAT GRADUATES SAY">Real change, in their words</SectionHead>
        <div style={{ columnGap: "1rem", columnWidth: 280 }}>
          {REAL_TESTIMONIALS.map((t, i) => (
            <div key={i} className="hub-card" data-testid={`testimonial-${i}`} style={{ padding: "1.4rem", marginBottom: "1rem", breakInside: "avoid", display: "inline-block", width: "100%" }}>
              <div style={{ color: "#f0c869", marginBottom: ".5rem" }}>{"★★★★★"}</div>
              <p style={{ color: "#e7d9c9", fontStyle: "italic", lineHeight: 1.6, margin: 0 }}>"{t.quote}"</p>
              <strong style={{ color: "#fff", display: "block", marginTop: ".8rem" }}>— {t.name}</strong>
              <span className="muted" style={{ fontSize: ".78rem" }}>Verified graduate</span>
            </div>
          ))}
        </div>
      </div></section>

      <CtaBand testid="band3-cta" line="Still reading? That's the part of you that's ready. Begin now." />

      {/* FAQ */}
      <section className="sec pad" data-testid="faq" style={{ background: "#17160f" }}><div className="wrap" style={{ maxWidth: "72ch" }}>
        <SectionHead kicker="EVERY QUESTION, ANSWERED">Your questions, handled</SectionHead>
        <div style={{ display: "grid", gap: ".6rem" }}>
          {SHARED_FAQ.map((f, i) => (
            <div key={i} className="hub-card" style={{ padding: 0, overflow: "hidden" }}>
              <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", padding: "1rem 1.2rem", background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "left", fontWeight: 600 }}>
                {f.q}
                <ChevronDown size={18} style={{ flexShrink: 0, transform: openFaq === i ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
              </button>
              {openFaq === i && <p className="muted" style={{ padding: "0 1.2rem 1.1rem", margin: 0, lineHeight: 1.7 }}>{f.a}</p>}
            </div>
          ))}
        </div>
      </div></section>

      {/* FINAL CTA */}
      <section className="sec pad" data-testid="final-cta" style={{ textAlign: "center", background: "radial-gradient(900px 400px at 50% 0%,rgba(199,146,30,.35),transparent 60%)" }}>
        <div className="wrap" style={{ maxWidth: "60ch" }}>
          <h2 style={{ color: "#fff", fontSize: "clamp(1.8rem,4vw,2.6rem)" }}>The patterns won't change by themselves.</h2>
          <p className="lead" style={{ color: "#e7d9c9" }}>You can close this page and have the same day tomorrow — or you can learn, finally, how to run your own mind. {c.certs.length} certifications. Two guarantees. One decision.</p>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1.4rem" }}><CtaButtons testid="final-cta-btns" /></div>
          <p className="muted" style={{ marginTop: "1rem", display: "inline-flex", alignItems: "center", gap: ".4rem" }}><Lock size={14} /> Secure PayPal checkout · instant access to your learning hub</p>
        </div>
      </section>

      {/* Sticky mobile enrol bar */}
      <div className="mobile-cta" style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 60, background: "rgba(16,15,13,.98)", borderTop: "1px solid #2a271f", padding: ".55rem .9rem", alignItems: "center", justifyContent: "space-between", gap: ".6rem" }}>
        <div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: ".95rem" }}>{gbp(priceFull)}{ebActive && <span style={{ textDecoration: "line-through", opacity: .55, marginLeft: 5, fontWeight: 600, fontSize: ".8rem" }}>{gbp(c.full)}</span>}</div>
          <div className="muted" style={{ fontSize: ".7rem" }}>{ebActive ? `${eb.discount_pct}% early-bird · ${eb.remaining} left` : `or ${gbp(priceMonthly)}/mo · ${c.certs.length} certs`}</div>
        </div>
        <a href="#enrol" className="btn" data-testid="mobile-enrol-btn" style={{ background: "#C7921E", color: "#161616", fontWeight: 800, padding: ".6rem 1.3rem" }}>Enrol now <ArrowRight size={15} /></a>
      </div>

      {leadOpen && (
        <div data-testid="lead-modal" role="dialog" aria-modal="true"
          style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(0,0,0,.72)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.2rem" }}
          onClick={(e) => { if (e.target === e.currentTarget && !leadBusy) setLeadOpen(false); }}>
          <form onSubmit={submitLead} data-testid="lead-form"
            style={{ width: "100%", maxWidth: 420, background: "#1b1a17", border: "1px solid #3a352c", borderRadius: 16, padding: "1.6rem", boxShadow: "0 24px 70px rgba(0,0,0,.6)" }}>
            <h3 style={{ color: "#fff", margin: "0 0 .3rem", fontSize: "1.25rem" }}>Reserve your place</h3>
            <p className="muted" style={{ fontSize: ".85rem", margin: "0 0 1.1rem" }}>
              {user ? "Just confirm your mobile number to continue." : "Enter your details to continue to secure enrolment."}
            </p>
            <label style={{ display: "block", color: "#e7d9c9", fontSize: ".8rem", marginBottom: ".25rem" }}>Full name</label>
            <input data-testid="lead-name" value={leadForm.name} disabled={!!user?.name}
              onChange={(e) => setLeadForm((f) => ({ ...f, name: e.target.value }))}
              style={{ width: "100%", padding: ".6rem .7rem", marginBottom: ".8rem", borderRadius: 9, border: "1px solid #3a352c", background: user?.name ? "#15140f" : "#141311", color: "#fff" }} />
            <label style={{ display: "block", color: "#e7d9c9", fontSize: ".8rem", marginBottom: ".25rem" }}>Email</label>
            <input data-testid="lead-email" type="email" value={leadForm.email} disabled={!!user?.email}
              onChange={(e) => setLeadForm((f) => ({ ...f, email: e.target.value }))}
              style={{ width: "100%", padding: ".6rem .7rem", marginBottom: ".8rem", borderRadius: 9, border: "1px solid #3a352c", background: user?.email ? "#15140f" : "#141311", color: "#fff" }} />
            <label style={{ display: "block", color: "#e7d9c9", fontSize: ".8rem", marginBottom: ".25rem" }}>Mobile number</label>
            <input data-testid="lead-mobile" type="tel" value={leadForm.mobile} autoFocus
              onChange={(e) => setLeadForm((f) => ({ ...f, mobile: e.target.value }))}
              placeholder="e.g. 07700 900000"
              style={{ width: "100%", padding: ".6rem .7rem", marginBottom: "1.1rem", borderRadius: 9, border: "1px solid #3a352c", background: "#141311", color: "#fff" }} />
            <button type="submit" className="btn" data-testid="lead-submit" disabled={leadBusy}
              style={{ width: "100%", background: "#C7921E", color: "#161616", fontWeight: 800, justifyContent: "center", padding: ".8rem 1.2rem" }}>
              {leadBusy ? "Saving…" : <>Continue to enrolment <ArrowRight size={16} /></>}
            </button>
            <p className="muted" style={{ fontSize: ".72rem", textAlign: "center", margin: ".8rem 0 0" }}>
              We'll only use these to help you enrol and follow up about this training.
            </p>
          </form>
        </div>
      )}

      <Footer />
    </div>
  );
}
